import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, X, RotateCcw } from "lucide-react";

import { PLEDGE, money } from "@/lib/pledge";
import { useNavAutoHide, useSmoothScroll } from "@/lib/pageScroll";

/*
 * PAPER
 *
 * The homepage runs its own visual world, scoped to `.lp` in index.css and shared with nothing
 * else in the product: warm paper ground, five working colours each owning one meaning, generous
 * radii. It is single-theme and carries no theme toggle — the tokens are redefined absolutely
 * rather than as a `:root` override, so the page looks the same however the app is themed, and a
 * visitor who has never signed in has nothing to theme.
 *
 * Four things on this page are live rather than pictures of live: the 30-day plan, the commitment
 * ledger, the track picker and the graveyard tally. Nothing here is a screenshot.
 */

// The pledge is priced by region — ₹2,000 in India, $99 elsewhere. See lib/pledge.js.
const STAKE = PLEDGE.amount;
const TOTAL_DAYS = 30;

/*
 * The thirty days.
 *
 * Previously this page held four WEEK objects and derived a day's content with
 * `Math.floor((day - 1) / 7.5)`, which meant every day inside a block rendered the same task —
 * tapping day 3 and day 7 showed identical text. The section exists to argue that the plan is
 * daily, and it was demonstrating the opposite.
 *
 * So all thirty are written out, each with the three tasks the product actually promises (three
 * a day, 20–45 minutes each) and the proof that closes it. These are an ILLUSTRATION of a plan,
 * not the plan: the real one is generated per person from their assessment, which is why the
 * section says "a plan like this" rather than "your plan".
 *
 * [title, [[task, minutes] x3], proof]
 */
const BLOCKS = [
  { name: "Prove the base", hue: "blue", from: 1, to: 8 },
  { name: "Build the thing", hue: "green", from: 9, to: 15 },
  { name: "Tell the story", hue: "violet", from: 16, to: 23 },
  { name: "Get in the room", hue: "coral", from: 24, to: 30 },
];

const DAYS = [
  ["Name the gap you keep avoiding",
    [["Answer the eight assessment questions honestly", 25], ["Pick one target role — not three", 20], ["Write the one weakness you dodge in interviews", 20]],
    "your target role, in writing"],
  ["Break the weakness into something buildable",
    [["Turn the weakness into three concrete sub-skills", 25], ["Find one exercise that exposes the first", 20], ["Do it badly, on purpose, and keep the file", 30]],
    "a commit link"],
  ["Fix what yesterday exposed",
    [["Reread yesterday's code and mark what broke", 20], ["Rewrite the worst function properly", 35], ["Write two sentences on what you had wrong", 20]],
    "a commit link"],
  ["Read the manual you skipped",
    [["Find the official docs page for your weak spot", 20], ["Build the smallest example the docs describe", 35], ["Note one thing the docs do that you never did", 20]],
    "a commit link"],
  ["Make it fail",
    [["Write a test that proves yesterday's example works", 30], ["Break it on purpose and watch the test catch it", 20], ["Add the edge case you would have shipped", 25]],
    "a passing test run"],
  ["Read someone else's version",
    [["Find a real repo that solves your weak spot", 25], ["Read one file end to end, no skimming", 30], ["Steal one pattern into your own example", 25]],
    "a commit link"],
  ["Explain it out loud",
    [["Write the concept in plain English, no jargon", 25], ["Cut it to five sentences", 20], ["Record yourself saying it in under a minute", 20]],
    "a written explanation"],
  ["Close block one",
    [["Redo day two's exercise from memory", 35], ["Diff it against the original", 20], ["Take the week-one assessment", 25]],
    "a passing assessment"],
  ["Choose the thing you'll ship",
    [["Pick one project a hiring manager could open", 25], ["Write what it does in one sentence", 20], ["Cut every feature that isn't that sentence", 25]],
    "a one-line scope"],
  ["Make it run on your machine",
    [["Initialise the repo and commit the empty shell", 20], ["Get one route or screen rendering", 35], ["Push it before you stop", 20]],
    "a commit link"],
  ["Make the smallest thing work",
    [["Build the single feature the sentence promises", 45], ["Handle the one input that breaks it", 25], ["Commit with a message that says what changed", 20]],
    "a commit link"],
  ["Put it somewhere with a URL",
    [["Deploy it, however ugly", 40], ["Fix whatever only broke in production", 30], ["Send the link to one person", 20]],
    "a deployed URL"],
  ["Make it survive a stranger",
    [["Watch someone use it without helping them", 25], ["Fix the thing they got stuck on first", 35], ["Add the empty state you forgot", 25]],
    "a deployed URL"],
  ["Write the README you'd want",
    [["What it does, why you built it, how to run it", 30], ["Add one screenshot or a ten-second clip", 25], ["Delete every sentence that flatters you", 20]],
    "a README link"],
  ["Close block two",
    [["Fix the last thing that embarrasses you", 40], ["Tag a release and write the changelog", 25], ["Take the week-two assessment", 25]],
    "a passing assessment"],
  ["Turn the build into a story",
    [["Write the situation and the task, plainly", 30], ["Write what YOU did, not what the team did", 30], ["Find the one number you actually have", 20]],
    "a written STAR answer"],
  ["Write the second story",
    [["Pick a time something went wrong", 25], ["Write what you changed once you knew", 30], ["End on the result, not the apology", 20]],
    "a written STAR answer"],
  ["Write the story about a disagreement",
    [["Pick a technical call you argued for or against", 25], ["Write the reasoning on both sides fairly", 30], ["Say what happened and what you'd redo", 25]],
    "a written STAR answer"],
  ["Rebuild the resume around proof",
    [["Replace every duty line with a result line", 40], ["Put the thing you shipped this month at the top", 25], ["Cut it to one page", 25]],
    "a resume PDF"],
  ["Make the resume survive six seconds",
    [["Give it to someone for six seconds, then ask what they remember", 20], ["Fix whatever they didn't remember", 35], ["Run it past the keyword check for your role", 25]],
    "a resume PDF"],
  ["Rewrite the profile people actually read",
    [["Rewrite the headline to name the role you want", 25], ["Rewrite the about section as one story", 35], ["Add the project with its live link", 20]],
    "a profile link"],
  ["Say the stories out loud",
    [["Record yourself answering all three, cold", 30], ["Watch it back and mark every filler sentence", 25], ["Record the worst one again", 25]],
    "a recording"],
  ["Close block three",
    [["Run one full mock interview", 45], ["Write down the two questions that hurt", 20], ["Take the week-three assessment", 25]],
    "a passing assessment"],
  ["Build the list worth applying to",
    [["Find fifteen roles that match the sentence from day nine", 40], ["Cut the ten you're applying to out of fear", 20], ["Rank the five that are left", 20]],
    "a shortlist of five"],
  ["Send the first three, specifically",
    [["Write three notes that each reference something you built", 40], ["Send them", 20], ["Log what you sent and when", 20]],
    "three confirmations"],
  ["Find the humans behind the postings",
    [["Find one real person at each of the five", 30], ["Write one message that isn't about you", 30], ["Send two of them", 20]],
    "two sent messages"],
  ["Prepare for the room, not the quiz",
    [["Research one company properly — product, not press", 35], ["Write three questions only someone who looked would ask", 25], ["Practise the opening ninety seconds", 25]],
    "written questions"],
  ["Run the technical rehearsal",
    [["Do one timed problem out loud, narrating", 40], ["Redo it silently and compare", 25], ["Write the pattern you missed", 20]],
    "a written post-mortem"],
  ["Send the next three",
    [["Follow up on anything silent past five days", 25], ["Write and send three more specific applications", 40], ["Update the tracker", 20]],
    "three confirmations"],
  ["Close the thirty and keep the engine",
    [["Take the final assessment", 30], ["Write what changed since day one, with evidence", 30], ["Set next month's target and the first task", 25]],
    "a passing assessment"],
];

const blockOf = (day) => BLOCKS.findIndex((b) => day >= b.from && day <= b.to);

// [title, meta] — the percentages are gone; a completion figure invited a progress reading, and
// nothing here is in progress. Selecting rows is what the section does now.
const GRAVEYARD = [
  ["The complete system design bootcamp", "38 hours left · last opened 7 months ago"],
  ["Advanced TypeScript patterns", "11 hours left · last opened in March"],
  ["Rust for backend engineers", "26 hours left · never opened"],
  ["Kubernetes, the hard way", "9 hours left · last opened last winter"],
  ["Interview prep: 300 problems", "281 problems left · bookmarked twice"],
  ["That side project repo", "last commit 14 months ago"],
];

// Track → the hue it paints the plan in, and what proof honestly looks like on it. The product
// generates a different plan per track; this is the shape of that difference, not a mock of it.
const TRACKS = [
  ["Backend", "blue", "an endpoint, and the commit that shipped it"],
  ["Frontend", "violet", "a URL a stranger can click"],
  ["Full Stack", "green", "one feature, end to end, live"],
  ["Mobile", "coral", "a build running on a real device"],
  ["DevOps / SRE", "amber", "a pipeline run that goes green"],
  ["ML", "violet", "a notebook, and the metric that moved"],
  ["Product", "blue", "a written spec, and the no you said"],
];

const HOW = [
  ["blue", "Answer eight questions", "Your stack, your target, and what already stalled. Four minutes."],
  ["green", "Do three tasks a day", "20–45 minutes each, generated for the role you named — not a syllabus."],
  ["coral", "Prove it, or it doesn't count", "A commit, a URL, a written answer. There is no “in progress” to hide in."],
];

const SHELL = "max-w-[1200px] mx-auto px-[clamp(20px,5vw,64px)]";

/*
 * Reveal on scroll. One passive listener coalesced into a frame, sweeping a shrinking list and
 * detaching once everything is shown.
 *
 * Deliberately NOT IntersectionObserver: this measures position, so a restored scroll offset or
 * an instant jump — which is exactly what an in-page anchor does for a visitor who has asked for
 * reduced motion — is handled the same as an ordinary scroll. The hiding styles are gated behind
 * `.lp-ready`, added only here, so a page whose JS never runs shows everything.
 */
function useReveal() {
  const root = useRef(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    let pending = Array.from(el.querySelectorAll("[data-reveal]"));
    if (!pending.length) return;
    el.classList.add("lp-ready");

    let frame = 0;

    const detach = () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };

    const sweep = () => {
      frame = 0;
      const limit = window.innerHeight * 0.92;
      pending = pending.filter((node) => {
        if (node.getBoundingClientRect().top > limit) return true;
        node.classList.add("is-in");
        return false;
      });
      if (!pending.length) detach();
    };

    function schedule() {
      if (!frame) frame = requestAnimationFrame(sweep);
    }

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    sweep();

    return () => {
      detach();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return root;
}

// Section head. Number, coloured kicker, headline — repeated so four sections read as one page.
function Head({ n, hue, kicker, children, size = "clamp(32px,4.6vw,60px)", measure = "20ch" }) {
  return (
    <>
      <div className="flex items-center gap-3" data-reveal>
        <span
          className="m grid place-items-center rounded-full text-white"
          style={{ background: `rgb(var(--${hue}))`, width: 26, height: 26, fontSize: 11 }}
        >
          {n}
        </span>
        <span className="m" style={{ color: `rgb(var(--${hue}))` }}>{kicker}</span>
      </div>
      <h2
        className="d mt-6 mb-0"
        style={{ fontSize: size, maxWidth: measure }}
        data-reveal
      >
        {children}
      </h2>
    </>
  );
}

/*
 * The headline's last word, on a loop.
 *
 * "No more tabs." was one joke told once. These are the things the reader actually recognises
 * themselves in, and each is a different accusation: the twenty open tabs, the course bought and
 * abandoned, the restart every January, the plan that never became a day's work. Rotating them
 * lets the line make four arguments in the space of one, and — the practical reason — a headline
 * that moves is the difference between a page that looks finished and a page that looks alive.
 *
 * THEY ARE ALL SHORT, AND THAT IS A HARD CONSTRAINT RATHER THAN A STYLE. Each is set at up to 76px
 * in a compact face on the line "No more ___", inside a hero column measured at 516px. "No more "
 * eats 254px of that, leaving 262px for the word. The first cut of this list ignored that: only
 * "tabs." fitted, so every other word dropped to its own line and the headline silently became
 * four lines instead of the three it is built as.
 *
 * Measured at 76px against that 262px budget: drift. 141, lists. 142, tabs. 148, later. 149,
 * limbo. 182 — against restarts. 250 (5% headroom) and bookmarks. 350 (overflows outright).
 *
 * The words below all keep at least 30% headroom, which is deliberate slack rather than caution:
 * the real display face is licensed and absent, so this renders in the Archivo fallback today and
 * every width here shifts the day the licensed files are dropped in. Tuning to the pixel would
 * mean the headline breaks on the deploy that finally adds the font.
 */
const HERO_WORDS = ["tabs.", "later.", "drift.", "limbo.", "lists."];

// Long enough to read the whole line and take it in before it changes again. Under about two
// seconds the headline reads as a slideshow demanding attention rather than a line of copy.
const WORD_HOLD_MS = 2900;

function RollingWord() {
  // Reduced motion gets the first word and no rotation at all — text that changes under the
  // reader is motion whether or not it slides, and this one is the largest thing on the page.
  const [calm] = useState(
    () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  const [i, setI] = useState(0);

  useEffect(() => {
    if (calm) return undefined;
    const id = setInterval(() => setI((n) => (n + 1) % HERO_WORDS.length), WORD_HOLD_MS);
    return () => clearInterval(id);
  }, [calm]);

  if (calm) {
    return <span className="mark is-in" style={{ "--h": "var(--coral-lit)" }}>{HERO_WORDS[0]}</span>;
  }

  const previous = HERO_WORDS[(i - 1 + HERO_WORDS.length) % HERO_WORDS.length];

  return (
    // aria-live is deliberately absent: this is decorative repetition of a fixed set, and
    // announcing a new word every three seconds would make the page unusable with a screen
    // reader. The outgoing copy is hidden from the tree so the sentence only ever reads once.
    <span className="roll">
      {i > 0 && (
        <span key={`out-${i}`} className="roll-out" aria-hidden="true">{previous}</span>
      )}
      <span key={`in-${i}`} className="roll-in mark" style={{ "--h": "var(--coral-lit)" }}>
        {HERO_WORDS[i]}
      </span>
    </span>
  );
}

/*
 * The hero film.
 *
 * A ten-second loop that shows the product's argument rather than restating it, sitting under the
 * headline where a screenshot would normally go. Three things about it are deliberate:
 *
 * IT DOES NOT COMPETE WITH THE LCP. This section holds the largest paint on the page, and a 1MB
 * media fetch started during load takes bandwidth from the bundle and the headline's font. So the
 * element renders with its poster and NO `src`, and the source is attached only once the browser
 * is idle. `preload="none"` alone does not achieve this — an `autoplay` video overrides it and
 * fetches anyway, which is the trap this exists to avoid.
 *
 * IT RESERVES ITS SPACE. Explicit width/height plus an aspect-ratio box, so the poster, the video
 * and the empty state all occupy identical space and nothing below shifts when the file lands.
 * (The file itself is 1280x720 and silent — the audio track was stripped, since a muted autoplay
 * loop can never play it and it was a quarter of the bytes.)
 *
 * IT OBEYS reduced-motion. Somebody who asked the OS for less movement gets a still frame and a
 * play button instead of a loop running in their peripheral vision for as long as they read.
 */
const FILM = "/home_page/hero.mp4";
const FILM_POSTER = "/home_page/hero-poster.jpg";

function HeroFilm() {
  // Read once: this decides between two different elements, not a style, so re-reading it on
  // every render would buy nothing.
  const [calm] = useState(
    () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    // .bind(window) is load-bearing, not defensive style: these are Window methods, and calling
    // one through a bare local (`const f = window.requestIdleCallback; f(...)`) hands it an
    // undefined receiver, which Chrome rejects with "Illegal invocation". jsdom would never show
    // it — it has no requestIdleCallback, so tests take the setTimeout branch and pass either way.
    const idle = window.requestIdleCallback?.bind(window) || ((fn) => setTimeout(fn, 200));
    const cancel = window.cancelIdleCallback?.bind(window) || clearTimeout;
    // The timeout matters: requestIdleCallback can wait indefinitely on a busy page, and a hero
    // that silently never loads is worse than one that loads slightly early.
    const id = idle(() => setArmed(true), { timeout: 2500 });
    return () => cancel(id);
  }, []);

  return (
    /* The frame. `overflow-hidden` is what actually rounds the film — a radius on the container
       alone leaves the video's own square corners sitting on top of it.
       28px rather than the card scale's 18px: the radius has to read as curvature against a
       ~520px-wide object, and 18px on something this large looks like a rounding error rather
       than a decision.

       The shadow is built on `--scrim` (26 23 20 — the paper world's warm near-black), NOT on
       `--shadow`. `.lp` redefines its tokens absolutely and never sets `--shadow`, so that name
       inherits the app's COOL near-black here and drops a faintly blue shadow onto a warm paper
       page. `.lp .card` sidesteps the same trap by writing the warm value as a literal. */
    <div
      className="overflow-hidden"
      style={{
        borderRadius: 28,
        border: "1px solid rgb(var(--line))",
        boxShadow: "0 24px 60px -28px rgb(var(--scrim) / 0.30), 0 2px 8px -3px rgb(var(--scrim) / 0.10)",
      }}
    >
      <video
        className="block w-full"
        width={1280}
        height={720}
        poster={FILM_POSTER}
        src={armed ? FILM : undefined}
        autoPlay={!calm}
        controls={calm}
        muted
        loop
        playsInline
        preload="none"
        aria-label="Thirty days of Stride: three tasks a day, proof at the end of each, and a plan that adapts as you go."
        style={{ aspectRatio: "16 / 9", background: "rgb(var(--elevated))" }}
      />
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const root = useReveal();

  /* Scroll feel. Both live in lib/pageScroll.js rather than here, for two reasons: the momentum
     engine has to read document geometry, which this file is deliberately barred from doing
     outside useReveal (see test_layout_reads_are_confined_to_the_scroll_reveal), and its easing
     and edge-damping are arithmetic worth testing on their own. Both decline to do anything at
     all under prefers-reduced-motion. */
  useSmoothScroll();
  const navHidden = useNavAutoHide();

  const [day, setDay] = useState(9);
  const [kept, setKept] = useState(6);
  const [missed, setMissed] = useState(1);
  const [track, setTrack] = useState(0);
  const [owned, setOwned] = useState([]); // graveyard rows the visitor admits to

  /*
   * Every CTA on this page lands on /login, which is the one door for both halves of the
   * audience: it carries the sign-in and the create-account modes on the same screen, and App.js
   * routes a fresh account onward to /onboard/smart by itself. Pointing at /onboard/smart
   * directly, as this used to, sent a logged-out visitor into a RequireAuth bounce.
   */
  const startNow = () => navigate("/login");
  // scrollIntoView does not consult the reduced-motion query on its own.
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });

  const perDay = STAKE / TOTAL_DAYS;
  const balance = Math.max(0, STAKE - missed * perDay);
  const settled = kept + missed;

  const blockIdx = blockOf(day);
  const block = BLOCKS[blockIdx];
  const [dayTitle, dayTasks, dayProof] = DAYS[day - 1];
  const trackHue = TRACKS[track][1];

  const toggleOwned = (i) =>
    setOwned((o) => (o.includes(i) ? o.filter((v) => v !== i) : [...o, i]));

  return (
    <div ref={root} className="lp min-h-screen" style={{ overflowX: "clip" }}>
      {/* ── Nav ───────────────────────────────────────────────────────────── */}
      {/* Detached from the top edge and floating: the shell keeps it aligned with the page's
          measure, and `.lp-bar` is the glass pill itself. Positioning lives in CSS (`.lp .lp-nav`)
          because the hide transform has to know the gap, and a number in two places drifts. */}
      <nav
        className={`lp-nav ${SHELL}`}
        data-hidden={navHidden ? "true" : undefined}
      >
        <div className="lp-bar flex items-center gap-4 sm:gap-7">
          <span className="d-sm mr-auto" style={{ fontSize: 23 }}>Stride</span>

          {[["problem", "The problem"], ["plan", "The plan"], ["stake", "The stake"]].map(([id, text]) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="hidden md:block text-sm font-medium text-ink-2 hover:text-ink transition-colors"
            >
              {text}
            </button>
          ))}

          {/* The recruiter app, served from /recruiter on this same origin. It used to point at
              recruiter.stridemylife.com, which has never resolved — this was a dead link on the
              live homepage. A plain href, not navigate(): /recruiter is a separate bundle that
              React Router here knows nothing about, so it has to be a real document load. */}
          <a
            href="/recruiter"
            className="hidden lg:block text-sm font-medium text-ink-2 hover:text-ink transition-colors"
          >
            For recruiters &#8599;
          </a>
          <button onClick={() => navigate("/login")} className="text-sm font-medium text-ink-2 hover:text-ink transition-colors px-2 py-2.5 -mx-2">
            Login
          </button>
          <button
            onClick={startNow}
            className="rounded-full bg-accent text-white text-sm font-bold px-5 py-2.5 hover:bg-accent-hover transition-colors whitespace-nowrap"
          >
            <span className="sm:hidden">Start</span>
            <span className="hidden sm:inline">Start day one</span>
          </button>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────────────────
          Holds the LCP element, so nothing here waits on a scroll listener.

          `z-0` is not decoration — without it the two drift shapes below DO NOT RENDER AT ALL.
          They sit at `-z-10`, and a negative layer paints against the nearest ancestor that forms
          a stacking context. `relative` alone does not form one, and neither does `.lp` (no
          z-index, and `overflow-x: clip` does not create one either), so they resolved against the
          ROOT — which paints them underneath `.lp`'s own background-color. Verified in Chrome:
          adding this one class is the difference between flat paper and the blue wash behind the
          film. See test_the_hero_ambient_shapes_are_actually_visible.

          The nav is a sibling at `z-30`, so it still stacks above this. */}
      <section className={`relative z-0 pt-[clamp(44px,7vh,84px)] pb-[clamp(48px,7vh,80px)] ${SHELL}`}>
        <div
          aria-hidden="true"
          className="drift pointer-events-none absolute -z-10 rounded-full"
          style={{ right: "-140px", top: "-120px", width: 420, height: 420, background: "rgb(var(--blue-soft))" }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -z-10 rounded-full"
          style={{ left: "-150px", bottom: "10%", width: 260, height: 260, background: "rgb(var(--amber-soft))" }}
        />

        {/* Two columns from `lg` up: the promise on the left, the film in the space it used to
            leave empty on the right. Below `lg` they stack and the film follows the buttons.

            The ratio is 1.04fr / 1fr rather than 1fr / 1fr. A 16:9 film is a WIDE object and the
            headline is a tall one, so splitting the shell down the middle makes the text column
            the tight one — it has to hold a three-line display headline, and it loses. The extra
            sliver goes to the type.

            `items-center` rather than `items-start`: the film is shorter than the text column, so
            aligning tops leaves it hanging with a pocket of dead space beneath — exactly the
            imbalance this change set out to remove. */}
        <div className="grid gap-[clamp(36px,4.5vw,68px)] lg:grid-cols-[minmax(0,1.04fr)_minmax(0,1fr)] lg:items-center">
          <div>
            <span
              className="m inline-flex items-center gap-2 rounded-full px-3.5 py-2"
              style={{ background: "rgb(var(--green-soft))", color: "rgb(var(--green))" }}
            >
              <span className="rounded-full" style={{ width: 6, height: 6, background: "rgb(var(--green-lit))" }} />
              For developers who keep starting over
            </span>

            {/* Down from a 104px ceiling. Half the shell cannot hold the old size: at 104px the
                longest line ran ~570px into a ~520px column and the `13ch` measure would have
                broken it somewhere the <br/>s do not. 76px is the largest that still sets three
                clean lines at the narrowest lg width. */}
            <h1 className="d mt-7 mb-0" style={{ fontSize: "clamp(44px,5.6vw,76px)", maxWidth: "13ch" }}>
              Forty-five days.<br />
              One plan.<br />
              No more <RollingWord />
            </h1>

            <p className="mt-7 mb-0 text-ink-2" style={{ fontSize: "clamp(17px,1.6vw,20px)", lineHeight: 1.55, maxWidth: "40ch" }}>
              Three tasks a day. Proof, not checkboxes. Stride is free — the only money involved is
              your own pledge, and you get it back.
            </p>

            {/* The reassurance line used to sit inside this flex row and wrap to its own line
                anyway. In a half-width column it always does, so it is its own block — which also
                lets it sit under both buttons instead of trailing the second one. */}
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <button
                onClick={startNow}
                className="rounded-full bg-accent text-white font-bold hover:bg-accent-hover transition-colors"
                style={{ fontSize: 16, padding: "16px 30px" }}
              >
                Start day one
              </button>
              <button
                onClick={() => scrollTo("plan")}
                className="rounded-full border border-line-strong font-bold hover:bg-elevated transition-colors"
                style={{ fontSize: 16, padding: "16px 26px" }}
              >
                See a plan
              </button>
            </div>
            <p className="mt-4 mb-0 text-ink-3 text-sm">Free to use. One small pledge, refunded when you finish.</p>
          </div>

          {/* See HeroFilm. */}
          <HeroFilm />
        </div>

        {/* How it works, in three cards. This is the section that has to do the explaining, so it
            sits above the fold's edge rather than four screens down. */}
        <div className="grid gap-4 sm:grid-cols-3 mt-[clamp(44px,6vh,72px)]">
          {HOW.map(([hue, title, body], i) => (
            <div
              key={title}
              className="card card-lift p-6"
              data-reveal
              style={{ "--d": `${i * 70}ms` }}
            >
              <span
                className="m grid place-items-center rounded-full text-white"
                style={{ background: `rgb(var(--${hue}))`, width: 28, height: 28 }}
              >
                {i + 1}
              </span>
              <p className="d-sm mt-4 mb-0" style={{ fontSize: 20 }}>{title}</p>
              <p className="mt-2.5 mb-0 text-ink-2" style={{ fontSize: 14.5, lineHeight: 1.6 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 01 · The problem ────────────────────────────────────────────────
          The graveyard, answered rather than read. Marking a row is its own feedback — it used
          to also print a running verdict underneath, which restated the headline in worse words
          every time it changed. */}
      <section id="problem" className={`band band-white pt-[clamp(56px,9vh,104px)] pb-[clamp(44px,7vh,80px)] ${SHELL}`}>
        <Head n="01" hue="amber" kicker="The problem">
          You already bought the <span className="mark" data-reveal style={{ "--h": "var(--amber-lit)" }}>knowledge</span>.
        </Head>

        <div className="grid gap-3 sm:grid-cols-2 mt-11" data-reveal>
          {GRAVEYARD.map(([title, meta], i) => {
            const on = owned.includes(i);
            return (
              <button
                key={title}
                onClick={() => toggleOwned(i)}
                aria-pressed={on}
                className="card card-lift flex items-center gap-4 text-left p-5"
                style={on ? { borderColor: "rgb(var(--amber))", background: "rgb(var(--amber-soft))" } : undefined}
              >
                <span
                  className="grid place-items-center rounded-full flex-none transition-colors"
                  style={{
                    width: 30, height: 30,
                    background: on ? "rgb(var(--amber))" : "rgb(var(--elevated))",
                    color: on ? "#fff" : "rgb(var(--ink-2))",
                  }}
                >
                  {on ? <Check size={15} strokeWidth={3} /> : <span style={{ fontSize: 12, fontWeight: 700 }}>{i + 1}</span>}
                </span>
                <span className="min-w-0">
                  <span className="block font-bold leading-[1.3]" style={{ fontSize: 15.5 }}>{title}</span>
                  <span className="block text-ink-3 mt-1" style={{ fontSize: 13 }}>{meta}</span>
                </span>
              </button>
            );
          })}
        </div>

      </section>

      {/* ── 02 · The plan ───────────────────────────────────────────────────
          Thirty distinct days, each with its own three tasks. The track picker recolours the
          section through a single `--track` variable rather than a class per colour. */}
      <section id="plan" data-reveal className={`band band-dots pt-[clamp(56px,9vh,104px)] pb-[clamp(44px,7vh,80px)] ${SHELL}`}>
        <Head n="02" hue="green" kicker="The plan" measure="24ch">
          A different task every single day.
        </Head>

        <p className="mt-6 mb-0 text-ink-2" style={{ fontSize: 17, lineHeight: 1.6, maxWidth: "48ch" }} data-reveal>
          Not a syllabus you fall behind on. Thirty days, each with its own three tasks, each
          needing proof.
        </p>

        {/* Track picker. Selecting one repaints the plan through --track. */}
        <div className="flex flex-wrap gap-2 mt-8" data-reveal>
          {TRACKS.map(([name, hue], i) => (
            <button
              key={name}
              onClick={() => setTrack(i)}
              aria-pressed={i === track}
              className="rounded-full text-sm font-bold px-4 py-2.5 border transition-colors"
              style={
                i === track
                  ? { background: `rgb(var(--${hue}))`, borderColor: `rgb(var(--${hue}))`, color: "#fff" }
                  : { background: "rgb(var(--surface))", borderColor: "rgb(var(--line))", color: "rgb(var(--ink-2))" }
              }
            >
              {name}
            </button>
          ))}
        </div>

        <div
          className="grid gap-[clamp(24px,3.5vw,48px)] items-start lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] mt-6 sm:mt-9"
          style={{ "--track": `var(--${trackHue})`, "--track-soft": `var(--${trackHue}-soft)` }}
        >
          <div data-reveal>
            {BLOCKS.map((b, bi) => (
              <div key={b.name} className="mb-4 sm:mb-6 last:mb-0">
                <div className="flex items-baseline justify-between gap-3 mb-2 sm:mb-3">
                  <span className="m" style={{ color: `rgb(var(--${b.hue}))` }}>{b.name}</span>
                  <span className="m text-ink-3">Days {b.from}&ndash;{b.to}</span>
                </div>
                {/* Eight columns, not seven: two of the four blocks are eight days long and were
                    wrapping to a second row on a phone while the other two sat on one, so the
                    section read as four ragged shapes instead of four bands. Eight fits the
                    longest block exactly; the seven-day blocks simply leave the last cell empty.
                    From `sm:` up there is room for the block's own length. */}
                <div className="grid grid-cols-8 gap-1 sm:grid-cols-7 sm:gap-2 lg:flex lg:flex-wrap">
                  {Array.from({ length: b.to - b.from + 1 }, (_, k) => b.from + k).map((n) => {
                    const isNow = n === day;
                    const walked = n < day;
                    return (
                      <button
                        key={n}
                        onClick={() => setDay(n)}
                        aria-label={`Day ${n}: ${DAYS[n - 1][0]}`}
                        aria-current={isNow ? "true" : undefined}
                        className="grid place-items-center rounded-xl font-bold border transition-all duration-150 w-full aspect-square lg:w-11 lg:h-11 lg:aspect-auto"
                        style={{
                          fontSize: 13,
                          background: isNow ? "rgb(var(--track))" : walked ? "rgb(var(--track-soft))" : "rgb(var(--surface))",
                          borderColor: isNow ? "rgb(var(--track))" : walked ? "rgb(var(--track-soft))" : "rgb(var(--line))",
                          color: isNow ? "#fff" : walked ? "rgb(var(--track))" : "rgb(var(--ink-3))",
                          transform: isNow ? "translateY(-2px)" : undefined,
                        }}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div
              className="rounded-2xl p-4 sm:p-5 mt-6 sm:mt-8"
              style={{ background: "rgb(var(--track-soft))" }}
            >
              <span className="m" style={{ color: "rgb(var(--track))" }}>Proof on the {TRACKS[track][0]} track</span>
              <p className="d-sm mt-2.5 mb-0" style={{ fontSize: 18 }}>{TRACKS[track][2]}</p>
            </div>
          </div>

          {/* The day card. Sticky on wide screens, because the point is watching it change. */}
          <div className="lg:sticky lg:top-[90px]" data-reveal style={{ "--d": "70ms" }}>
            <div className="card p-[clamp(20px,2.6vw,32px)]">
              <div className="flex items-center justify-between gap-3">
                <span
                  key={day}
                  className="pop m rounded-full px-3 py-1.5 text-white"
                  style={{ background: "rgb(var(--track))" }}
                >
                  Day {day}
                </span>
                <span className="m text-ink-3">{block.name}</span>
              </div>

              <h3 className="d-sm mt-5 mb-0" style={{ fontSize: "clamp(22px,2.4vw,30px)" }}>{dayTitle}</h3>

              <ol className="list-none p-0 mt-6 mb-0">
                {dayTasks.map(([task, mins], i) => (
                  <li key={task} className="flex gap-3.5 items-start py-3 border-t border-line first:border-t-0 first:pt-0">
                    <span
                      className="grid place-items-center rounded-full flex-none font-bold"
                      style={{ width: 24, height: 24, fontSize: 11, background: "rgb(var(--elevated))", color: "rgb(var(--ink-2))" }}
                    >
                      {i + 1}
                    </span>
                    <span className="flex-1" style={{ fontSize: 15, lineHeight: 1.5 }}>{task}</span>
                    <span className="text-ink-3 flex-none pt-1 font-semibold" style={{ fontSize: 12 }}>{mins} min</span>
                  </li>
                ))}
              </ol>

              <div
                className="flex items-center gap-2.5 rounded-xl mt-6 px-4 py-3.5"
                style={{ background: "rgb(var(--green-soft))" }}
              >
                <Check size={16} strokeWidth={3} style={{ color: "rgb(var(--green))", flex: "none" }} />
                <span style={{ fontSize: 14 }}>
                  <span className="font-bold">Proof required:</span>{" "}
                  <span style={{ color: "rgb(var(--green))", fontWeight: 700 }}>{dayProof}</span>
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 mt-4 px-1">
              <button
                onClick={() => setDay((d) => Math.max(1, d - 1))}
                disabled={day === 1}
                className="text-sm font-bold text-ink-2 hover:text-ink disabled:opacity-35 transition-colors py-3 px-1 -mx-1"
              >
                &larr; Previous day
              </button>
              <button
                onClick={() => setDay((d) => Math.min(TOTAL_DAYS, d + 1))}
                disabled={day === TOTAL_DAYS}
                className="text-sm font-bold hover:opacity-80 disabled:opacity-35 transition-opacity py-3 px-1 -mx-1"
                style={{ color: "rgb(var(--accent))" }}
              >
                Next day &rarr;
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── 03 · The stake ──────────────────────────────────────────────────
          The ledger is live: the buttons move real numbers, so the loss is felt before signup
          rather than described. Loss is coral and progress is green, and they are never the same
          colour — the section's whole argument is that forward and backward look nothing alike. */}
      <section id="stake" className={`band band-white pt-[clamp(56px,9vh,104px)] pb-[clamp(44px,7vh,80px)] ${SHELL}`}>
        <Head n="03" hue="coral" kicker="The stake" measure="22ch">
          Missing a day should{" "}
          <span className="mark" data-reveal style={{ "--h": "var(--coral-lit)" }}>cost</span> you something.
        </Head>

        <div className="grid gap-[clamp(24px,4vw,60px)] items-start lg:grid-cols-2 mt-6 sm:mt-9">
          <div data-reveal>
            <p className="mt-0 mb-0 text-ink-2" style={{ fontSize: 17, lineHeight: 1.62, maxWidth: "44ch" }}>
              We built Stride to give something back, so we charge nothing for it. But a free
              thing gets treated like a free thing — opened once, bookmarked, forgotten. So we ask
              for the smallest pledge that still stings: {money(STAKE)}, your own money, from day
              one.
            </p>
            <p className="mt-4 sm:mt-5 mb-0 text-ink-2" style={{ fontSize: 15.5, lineHeight: 1.6, maxWidth: "42ch" }}>
              You get all of it back with interest the day you finish. Skip a day and{" "}
              <span className="font-bold" style={{ color: "rgb(var(--coral))" }}>{money(perDay)}</span>{" "}
              of it goes to the people who showed up. That is the whole business model: not our
              revenue, just a reason to come back tomorrow.
            </p>
          </div>

          <div className="card p-[clamp(20px,2.6vw,32px)]" data-reveal style={{ "--d": "70ms" }}>
            <span className="m text-ink-3">The commitment ledger</span>

            <p
              key={missed}
              className="pop d mt-4 mb-0"
              style={{ fontSize: "clamp(36px,5vw,62px)", color: missed ? "rgb(var(--coral))" : "rgb(var(--green))" }}
            >
              {money(balance)}
            </p>

            <p className="m mt-3 mb-0" style={{ color: missed ? "rgb(var(--coral))" : "rgb(var(--ink-3))" }}>
              {missed
                ? `↓ ${money(missed * perDay)} · ${missed} missed ${missed === 1 ? "day" : "days"}`
                : `${kept} days proven · nothing lost yet`}
            </p>

            {/* 30 bars in one row read as ~9px slivers at 375px -- too thin to register as days.
                Two rows of 15 below sm: keeps each bar a legible width on a phone; one row from
                sm: up, where there's room. */}
            <div className="grid grid-cols-[repeat(15,minmax(0,1fr))] sm:flex gap-[3px] mt-5 sm:mt-6">
              {Array.from({ length: TOTAL_DAYS }, (_, i) => (
                <span
                  key={i}
                  className="sm:flex-1 rounded-sm transition-colors duration-300"
                  style={{
                    height: 22,
                    background:
                      i < kept ? "rgb(var(--green-lit))"
                      : i < settled ? "rgb(var(--coral-lit))"
                      : "rgb(var(--elevated))",
                  }}
                />
              ))}
            </div>
            <div className="flex justify-between mt-2">
              <span className="m text-ink-3">Day 1</span>
              <span className="m text-ink-3">Day 30</span>
            </div>

            <div className="flex flex-wrap gap-2 mt-5 sm:mt-7">
              <button
                onClick={() => {
                  setKept((k) => Math.min(TOTAL_DAYS - missed, k + 1));
                  setDay((d) => Math.min(TOTAL_DAYS, d + 1));
                }}
                className="inline-flex items-center gap-2 rounded-full text-white text-sm font-bold px-5 py-3 transition-opacity hover:opacity-90"
                style={{ background: "rgb(var(--green))" }}
              >
                <Check size={15} strokeWidth={3} />
                Did the work
              </button>
              <button
                onClick={() => setMissed((m) => Math.min(TOTAL_DAYS - kept, m + 1))}
                className="inline-flex items-center gap-2 rounded-full text-sm font-bold px-5 py-3 border transition-colors hover:bg-elevated"
                style={{ borderColor: "rgb(var(--line-strong))" }}
              >
                <X size={15} strokeWidth={3} style={{ color: "rgb(var(--coral))" }} />
                Skipped it
              </button>
              <button
                onClick={() => { setKept(6); setMissed(1); setDay(9); }}
                className="inline-flex items-center gap-1.5 rounded-full text-sm font-bold px-4 py-3 text-ink-3 hover:text-ink transition-colors"
              >
                <RotateCcw size={14} strokeWidth={2.5} />
                Reset
              </button>
            </div>

            <p className="mt-5 sm:mt-6 mb-0 text-ink-3" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
              Finish all 30 and your pledge comes back with interest — funded by everyone who didn't.
            </p>
          </div>
        </div>
      </section>

      {/* ── Founder note ────────────────────────────────────────────────────── */}
      <section className={`pt-[clamp(28px,5vh,60px)] pb-[clamp(48px,7vh,84px)] ${SHELL}`}>
        <div
          className="rounded-[26px] p-[clamp(26px,4.4vw,60px)] grid gap-7 lg:grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)] items-end"
          style={{ background: "rgb(var(--violet-soft))" }}
          data-reveal
        >
          <p className="d-sm m-0" style={{ fontSize: "clamp(19px,2.4vw,31px)", lineHeight: 1.35, maxWidth: "28ch" }}>
            &ldquo;I spent two years being three months away from being ready. Every course I bought
            was free to ignore. So we made Stride free — and made showing up the only thing that
            costs you anything.&rdquo;
          </p>
          <p className="m m-0 lg:text-right" style={{ color: "rgb(var(--violet))" }}>&mdash; Founder, Stride</p>
        </div>
      </section>

      {/* ── Closing ─────────────────────────────────────────────────────────── */}
      <section className={`band band-warm pt-[clamp(20px,4vh,48px)] pb-[clamp(64px,9vh,108px)] ${SHELL}`}>
        <h2 className="d m-0" style={{ fontSize: "clamp(40px,7vw,92px)", maxWidth: "14ch" }} data-reveal>
          Stop planning to level up.{" "}
          <span className="mark" data-reveal style={{ "--h": "var(--green-lit)" }}>Walk day one.</span>
        </h2>

        <p className="mt-7 mb-0 text-ink-2" style={{ fontSize: 17, lineHeight: 1.6, maxWidth: "44ch" }} data-reveal>
          Every week you wait is another week of the same salary, the same role, the same
          conversation about what you're planning to do.
        </p>

        {/* One door. /login carries both modes — sign in for anyone who has started, create an
            account for anyone who hasn't — and App.js sends a brand-new account on to the
            assessment by itself. This used to be a three-field waitlist form whose button said
            "Start free assessment" and then went nowhere: it POSTed a lead and printed "check
            your email" for an email nothing sent. */}
        <div className="flex flex-wrap items-center gap-3 mt-9" data-reveal style={{ "--d": "70ms" }}>
          <button
            onClick={startNow}
            className="inline-flex items-center gap-2.5 rounded-full bg-accent text-white font-bold min-h-[56px] px-8 hover:bg-accent-hover transition-colors"
            style={{ fontSize: 16 }}
          >
            Start day one
            <ArrowRight size={17} strokeWidth={2.75} />
          </button>
          <button
            onClick={startNow}
            className="rounded-full border border-line-strong font-bold min-h-[56px] px-7 hover:bg-elevated transition-colors"
            style={{ fontSize: 16 }}
          >
            I already have an account
          </button>
        </div>

        <p className="mt-5 mb-0 text-ink-3 text-sm">Eight questions &middot; Four minutes &middot; Pledge {money(STAKE)}, refunded when you finish</p>
      </section>

      <footer className={`border-t border-line flex flex-wrap justify-between items-center gap-4 py-8 ${SHELL}`}>
        <span className="d-sm" style={{ fontSize: 19 }}>Stride</span>
        <span className="text-ink-3 text-sm">&copy; 2026 &middot; Built for developers who follow through</span>
        <a href="https://stridemylife.com" className="text-ink-3 text-sm hover:text-accent transition-colors">
          stridemylife.com
        </a>
      </footer>
    </div>
  );
}
